// src/feed/useResolvedImageUrl.ts
//
// Resolves a snapshot's image_path/preview_url field to a URL that
// can actually be set as <img src=...>. Three cases:
//
//   1. The string is already an http(s) URL or data URL —
//      passthrough.
//   2. The string is a Firebase Storage object path AND the
//      viewer owns the underlying file — fetch a download URL
//      via getDownloadURL(). Fast path; storage rules permit it.
//   3. The string is a Firebase Storage object path AND the
//      viewer does NOT own it — route through the
//      feedImageProxy Cloud Function. The function checks the
//      feed_item's visibility (owner / public / followers /
//      share_enabled) and 302-redirects to a 1h-expiry signed
//      URL.
//
// Why the proxy is necessary:
//   - Storage rules lock images/{uid}/... to owner-only direct
//     reads. That's the right default for private chat photos.
//   - Feed posts can reference those paths under a public or
//     followers visibility setting — without the proxy,
//     non-owners just see broken images.
//   - The proxy is the single place where feed visibility logic
//     authoritatively meets storage access. If rule logic
//     changes later, update both files in lockstep.
//
// Results are cached in a module-level Map keyed by storage
// path so the same image rendered in a list (feed, profile
// grid) doesn't re-fetch its URL on every mount or scroll.
//
// Failures resolve to null so the caller can render a
// fallback.

import { useEffect, useRef, useState } from 'react';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { auth, storage } from '../services/firebase';

/** Cloud Functions origin. Public-by-design — the function does
 *  its own auth check via the bearer token we send. */
const FUNCTIONS_BASE =
  'https://us-central1-travelx-46b7a.cloudfunctions.net';

const cache = new Map<string, string>();
const inflight = new Map<string, Promise<string | null>>();

const isAlreadyUrl = (s: string): boolean =>
  /^(https?:|data:|blob:)/i.test(s);

/** A canonical chat-photo storage path looks like:
 *  `images/{uid}/{convId}/{filename}`. The owner uid is the
 *  second segment. */
const ownerUidFromPath = (path: string): string | null => {
  const parts = path.split('/');
  if (parts.length >= 2 && parts[0] === 'images') {
    return parts[1] || null;
  }
  return null;
};

/**
 * Build a feed-image-proxy URL. The function reads `image_path`
 * server-side from the feed_item doc; we only pass the
 * feed_item id. This stops a caller from supplying an
 * arbitrary storage path and tricking the function into signing
 * it.
 */
const buildProxyUrl = (feedItemId: string): string =>
  `${FUNCTIONS_BASE}/feedImageProxy?id=${encodeURIComponent(feedItemId)}`;

async function resolve(
  input: string,
  feedItemId?: string
): Promise<string | null> {
  if (!input) return null;
  if (isAlreadyUrl(input)) return input;

  // Per-input cache. We key on the input string AND the
  // feed_item id so a non-owner viewing the same path on two
  // different feed_items doesn't accidentally share a cached
  // URL whose visibility check passed for one but not the
  // other.
  const cacheKey = feedItemId ? `${input}::${feedItemId}` : input;
  const cached = cache.get(cacheKey);
  if (cached) return cached;
  const pending = inflight.get(cacheKey);
  if (pending) return pending;

  const promise = (async () => {
    const ownerUid = ownerUidFromPath(input);
    const viewerUid = auth.currentUser?.uid || null;

    // Fast path: viewer owns the file (or path doesn't have a
    // discernible owner — fall back to direct read attempt).
    if (!ownerUid || (viewerUid && ownerUid === viewerUid)) {
      try {
        const url = await getDownloadURL(storageRef(storage, input));
        cache.set(cacheKey, url);
        return url;
      } catch (err) {
        // Fall through to proxy path. A getDownloadURL failure
        // here typically means the rules denied us — exactly
        // the case the proxy exists to handle.
        if (!feedItemId) {
          // No feed item context — we can't proxy. Give up.
          console.warn(
            'useResolvedImageUrl: direct getDownloadURL failed and no feed_item_id available for proxy',
            input,
            err
          );
          return null;
        }
        // else: drop into proxy branch below.
      }
    }

    // Proxy path: feed_item id is required so the function can
    // resolve the real image_path itself + check visibility.
    if (!feedItemId) {
      console.warn(
        'useResolvedImageUrl: non-owner viewer with no feed_item_id; cannot proxy',
        input
      );
      return null;
    }

    // The proxy URL itself can be used as <img src> directly —
    // the browser follows the 302 redirect to the signed URL
    // automatically. We send the auth token via a query
    // parameter would NOT be safe (logs); the proper move is
    // a POST or a header. But <img> tags can't set headers.
    //
    // Trade-off: for share_enabled (publicly readable) feed
    // items the proxy works without auth. For
    // followers-visibility, we need auth — so we have to
    // pre-sign by calling the proxy via fetch (with the
    // bearer header) and following the redirect manually,
    // then caching the eventual signed URL.
    try {
      const headers: Record<string, string> = {};
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }
      const proxyUrl = buildProxyUrl(feedItemId);
      // `redirect: 'manual'` lets us read the Location header
      // ourselves rather than following + leaking the bearer
      // header to the storage host. Browsers in non-CORS
      // contexts may not surface the Location, so we also
      // accept the case where 'follow' resolves to the signed
      // URL directly.
      const resp = await fetch(proxyUrl, {
        method: 'GET',
        headers,
        redirect: 'follow',
      });
      if (!resp.ok) {
        console.warn(
          'useResolvedImageUrl: proxy returned',
          resp.status,
          'for',
          feedItemId
        );
        return null;
      }
      // resp.url is the final URL after redirects (the signed
      // storage URL). Use it directly as the image src.
      const finalUrl = resp.url;
      if (!finalUrl || finalUrl === proxyUrl) {
        // Some environments don't expose the redirect target
        // — fall back to using the proxy URL directly. The
        // browser will re-fetch and follow the redirect for
        // the <img> render.
        cache.set(cacheKey, proxyUrl);
        return proxyUrl;
      }
      cache.set(cacheKey, finalUrl);
      return finalUrl;
    } catch (err) {
      console.warn(
        'useResolvedImageUrl: proxy call failed for',
        feedItemId,
        err
      );
      return null;
    }
  })();
  inflight.set(cacheKey, promise);
  promise.finally(() => inflight.delete(cacheKey));
  return promise;
}

/** Resolve a single image_path-or-url to a renderable URL.
 *
 *  When `feedItemId` is provided, non-owner viewers route
 *  through the feedImageProxy Cloud Function (which enforces
 *  feed visibility before signing a storage URL). Without
 *  `feedItemId`, only the owner can resolve storage paths;
 *  others get null.
 *
 *  Always pass `feedItemId` from feed renderers — that's the
 *  whole point of the proxy. Non-feed surfaces (chat history,
 *  postcards on the user's own profile) can omit it; those are
 *  always owner-viewing-own-content.
 */
export function useResolvedImageUrl(
  input: string | null | undefined,
  feedItemId?: string
): string | null {
  const cacheKey = input
    ? feedItemId
      ? `${input}::${feedItemId}`
      : input
    : null;
  const [url, setUrl] = useState<string | null>(() => {
    if (!input) return null;
    if (isAlreadyUrl(input)) return input;
    return cacheKey ? cache.get(cacheKey) ?? null : null;
  });
  // Track the input we last kicked off resolution for so a
  // stale resolution doesn't overwrite a newer one if the prop
  // changes.
  const lastKeyRef = useRef<string | null>(cacheKey);

  useEffect(() => {
    lastKeyRef.current = cacheKey;
    if (!input) {
      setUrl(null);
      return;
    }
    if (isAlreadyUrl(input)) {
      setUrl(input);
      return;
    }
    const cached = cacheKey ? cache.get(cacheKey) : null;
    if (cached) {
      setUrl(cached);
      return;
    }
    let cancelled = false;
    resolve(input, feedItemId).then((resolved) => {
      if (cancelled) return;
      if (lastKeyRef.current !== cacheKey) return;
      setUrl(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [input, feedItemId, cacheKey]);

  return url;
}
